import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import axios from "../../api/axios";
import { getAttributeValue, getPlanExecutionStats } from "../../utils/utils";
import { Bar, Pie } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from "chart.js";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Annex2a = forwardRef(({ year, district, period, hideTableDis, assessmentStatus, assessmentStatus1, normalizedUserRole }, ref) => {
  const [tableData, setTableData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(parseInt(year));
  const [chartData, setChartData] = useState({});
  const [pieChartData, setPieChartData] = useState({});


  useEffect(() => {
    getAnnualActionPlan();
  }, [year, district, period]);

  useImperativeHandle(ref, () => ({
      getData: () => ({
        indicator: "Annex_2a",
        tableData
      }),
    }));

  async function getAnnualActionPlan() {
    try {
      const currentYear = year ? selectedYear : new Date().getFullYear();
      const years = [currentYear - 2, currentYear - 1, currentYear];

      const [yearTwoLess, yearOneLess, yearNow] = years;

      const tempAllData = [];

      for (const yr of years) {
        const result = await axios.get(
          `/tracker/trackedEntities?orgUnit=${district}&program=ArLnAxhykoz&filter=pWkYqcKukAY:EQ:${yr}&pageSize=5000`
        );

        const resp = await axios.get(
          `/tracker/events?program=ArLnAxhykoz&orgUnit=${district}&startDate=${yr}-01-01&endDate=${yr}-12-31&pageSize=5000`
        );

        if (result.data.instances.length > 0) {
          const aap = result.data.instances;
          const reports = resp.data.instances;

          const formattedPlans = aap.map((plan, idx) => ({
            index: idx,
            dd: getAttributeValue("Development Dimension", plan),
            date: plan.createdAt,
            trackedEntity: plan.trackedEntity,
          }));

          const counts = getPlanExecutionStats(formattedPlans, reports).map((item) => ({
            ...item,
            year: yr,
            percentage:
              item.planned > 0
                ? ((item.executed / item.planned) * 100).toFixed(1)
                : 0,
          }));

          tempAllData.push(...counts);
        }
      }

      // ✅ GROUP INTO REQUIRED FORMAT
      const grouped = {};

      // ✅ CLEAN DATA FIRST
      const cleanedData = tempAllData.filter(
        (item) =>
          item.dimension &&
          item.dimension !== "Total" &&
          item.dimension !== "N/A"
      );

      cleanedData.forEach((item) => {
        const { dimension, planned, executed, year } = item;

        if (!grouped[dimension]) {
          grouped[dimension] = {
            dimension,
            yearTwoLessPlan: 0,
            yearTwoLessExecuted: 0,
            yearOneLessPlan: 0,
            yearOneLessExecuted: 0,
            yearPlan: 0,
            yearExecuted: 0,
          };
        }

        if (year === yearTwoLess) {
          grouped[dimension].yearTwoLessPlan += planned;
          grouped[dimension].yearTwoLessExecuted += executed;
        }

        if (year === yearOneLess) {
          grouped[dimension].yearOneLessPlan += planned;
          grouped[dimension].yearOneLessExecuted += executed;
        }

        if (year === yearNow) {
          grouped[dimension].yearPlan += planned;
          grouped[dimension].yearExecuted += executed;
        }
      });

      let formattedData = Object.values(grouped);

      // ✅ ADD TOTAL ROW
      const total = {
        dimension: "Total",
        yearTwoLessPlan: 0,
        yearTwoLessExecuted: 0,
        yearOneLessPlan: 0,
        yearOneLessExecuted: 0,
        yearPlan: 0,
        yearExecuted: 0,
      };

      formattedData.forEach((row) => {
        total.yearTwoLessPlan += row.yearTwoLessPlan;
        total.yearTwoLessExecuted += row.yearTwoLessExecuted;
        total.yearOneLessPlan += row.yearOneLessPlan;
        total.yearOneLessExecuted += row.yearOneLessExecuted;
        total.yearPlan += row.yearPlan;
        total.yearExecuted += row.yearExecuted;
      });

      // ✅ ensure only ONE total at the end
      formattedData = formattedData.filter((row) => row.dimension !== "Total");
      formattedData.push(total);

      const order = [
        "Economic Development (ED)",
        "Social Development (SD)",
        "Implementation, Coordination, Monitoring And Evaluation",
        "Governance, Corruption And Public Accountability(GCPA)",
        "Environment, infrastructure and human settlement",
        "Emergency Planning And Covid-19 Response",
        "Total"
      ];

      formattedData.sort((a, b) => order.indexOf(a.dimension) - order.indexOf(b.dimension));

      // ✅ SET FINAL TABLE DATA
      setTableData(formattedData);

      // ---------------- CHARTS ----------------

      // Remove TOTAL for bar chart
      const chartRows = formattedData.filter(d => d.dimension !== "Total");

      // ---- BAR CHART ----
      const barChartData = {
        labels: chartRows.map(d =>
          d.dimension.length > 25
            ? d.dimension.substring(0, 25) + "..."
            : d.dimension
        ),

        datasets: [
          {
            label: `${yearTwoLess} Planned`,
            data: chartRows.map(d => d.yearTwoLessPlan),
            backgroundColor: "rgba(54, 162, 235, 0.6)",
          },
          {
            label: `${yearTwoLess} Executed`,
            data: chartRows.map(d => d.yearTwoLessExecuted),
            backgroundColor: "rgba(54, 162, 235, 1)",
          },
          {
            label: `${yearOneLess} Planned`,
            data: chartRows.map(d => d.yearOneLessPlan),
            backgroundColor: "rgba(255, 206, 86, 0.6)",
          },
          {
            label: `${yearOneLess} Executed`,
            data: chartRows.map(d => d.yearOneLessExecuted),
            backgroundColor: "rgba(255, 206, 86, 1)",
          },
          {
            label: `${yearNow} Planned`,
            data: chartRows.map(d => d.yearPlan),
            backgroundColor: "rgba(75, 192, 192, 0.6)",
          },
          {
            label: `${yearNow} Executed`,
            data: chartRows.map(d => d.yearExecuted),
            backgroundColor: "rgba(75, 192, 192, 1)",
          },
        ],
      };

      setChartData(barChartData);

      const pieChartData = {
        labels: ["Implemented", "Non-Implemented"],
        datasets: [
          {
            data: [
              total?.yearExecuted || 0,
              (total?.yearPlan || 0) - (total?.yearExecuted || 0),
            ],
            backgroundColor: [
              "rgba(75, 192, 192, 0.7)",
              "rgba(255, 99, 132, 0.7)",
            ],
          },
        ],
      };

      setPieChartData(pieChartData);

    } catch (err) {
      console.error("Error in getAnnualActionPlan:", err);
      setTableData([]);
      setChartData({});
      setPieChartData({});
    }
  }

  // // Bar chart options (reusing old logic)
  // const barChartOptions = {
  //   responsive: true,
  //   plugins: {
  //     legend: {
  //       position: "top",
  //     },
  //     title: {
  //       display: true,
  //       text: `Performance Comparison by Development Dimension (${new Date().getFullYear() - 2}-${new Date().getFullYear()})`,
  //     },
  //     tooltip: {
  //       callbacks: {
  //         label: (context) => `${context.dataset.label}: ${context.raw}%`,
  //       },
  //     },
  //   },
  //   scales: {
  //     y: {
  //       beginAtZero: true,
  //       max: 100,
  //       title: {
  //         display: true,
  //         text: "Percentage Executed (%)",
  //       },
  //     },
  //     x: {
  //       title: {
  //         display: true,
  //         text: "Development Dimensions",
  //       },
  //     },
  //   },
  // };

  // // Pie chart options (reusing old logic)
  // const pieChartOptions = {
  //   responsive: true,
  //   plugins: {
  //     legend: {
  //       position: "top",
  //     },
  //     title: {
  //       display: true,
  //       text: `Projects Implemented vs Non-Implemented (${year})`,
  //     },
  //     tooltip: {
  //       callbacks: {
  //         label: (context) => `${context.label}: ${context.raw} projects`,
  //       },
  //     },
  //   },
  // };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 30,
        },
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  // Dynamic paragraph based on tableData (reusing old logic)
  const dynamicParagraph = () => {
    if (tableData.length === 0) return null;

    const dimensionsData = tableData.filter((row) => row.dimension !== "Total");
    const totalRow = tableData.find((row) => row.dimension === "Total");
    const totalPlanned = totalRow ? totalRow.planned : 0;
    const totalExecuted = totalRow ? totalRow.executed : 0;

    // Group data by dimension and sum planned/executed
    const dimensionStats = dimensionsData.reduce((acc, row) => {
      if (!acc[row.dimension]) {
        acc[row.dimension] = { planned: 0, executed: 0 };
      }
      acc[row.dimension].planned += row.planned;
      acc[row.dimension].executed += row.executed;
      return acc;
    }, {});

    // Sort by planned activities to find the dimension with the highest planned
    const highestPlanned = Object.entries(dimensionStats).reduce((max, current) =>
      current[1].planned > max[1].planned ? current : max, ["", { planned: 0 }])[0];

    return (
      <p className="mt-3">
        From the above table, it can be noted that the Municipality placed premium on all the six development dimensions under the Agenda for Jobs II policy framework. It earmarked and implemented various interventions for a holistic development towards the achievement of the set goal. Development therefore was widely spread across the various sectors, and none skewed.
        <br />
        {Object.entries(dimensionStats).map(([dimension, stats]) => (
          <span key={dimension}>
            {/* Out of the {stats.planned} planned activities under {dimension}, {stats.executed} were executed. */}
            {dimension === Object.keys(dimensionStats).pop() ? "" : <br />}
          </span>
        ))}
        {/* This puts the total number of activities implemented at {totalExecuted} out of {totalPlanned} planned. */}
      </p>
    );
  };

  return (
    <div className="col-12">
      <h3>Annex 2a: Details on the RCC Annual Action Plan Implemented </h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">

          {/* <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table1-${year}`}
             hideTableDis={hideTableDis}
             assessmentStatus1={assessmentStatus1}   
          /> */}
          <div className="table-responsive">
            <table className="table table-bordered" style={{
              border: '1px solid #000',
              borderCollapse: 'collapse',
              width: '100%',
              marginTop: "20px"
            }}>
              <thead>
                <tr style={{ backgroundColor: '#d4edda', fontWeight: 'bold', border: '1px solid #000' }}>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>S/N</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Development Dimension</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }} colSpan="2">
                    {selectedYear - 2}</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }} colSpan="2">
                    {selectedYear - 1}</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold', textAlign: 'center' }} colSpan="2">
                    {selectedYear}</th>
                </tr>
                <tr style={{ fontWeight: 'bold', border: '1px solid #000' }}>
                  <th style={{ border: '1px solid #000' }}></th>
                  <th style={{ border: '1px solid #000' }}></th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Planned</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Executed</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Planned</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Executed</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Planned</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Executed</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, index) => {

                    return (
                      <tr key={index} style={
                        row.dimension === "Total"
                          ? { fontWeight: 'bold' }
                          : {}
                      }>
                        <td style={{ border: '1px solid #000', fontWeight: 'bold' }}>{index + 1}</td>
                        <td style={{ border: '1px solid #000' }}>{row.dimension}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearTwoLessPlan}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearTwoLessExecuted}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearOneLessPlan}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearOneLessExecuted}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearPlan}</td>
                        <td style={{ border: '1px solid #000' }}>{row.yearExecuted}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center" }}>
                      No data available for {year}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2">
            <small>Source: MPCU</small>
          </p>
          {/* {dynamicParagraph()} */}
          <hr />
          <h5>Performance Comparison by Development Dimension ({year - 2}-{year})</h5>
          {/* <div className="mt-10" style={{ height: "700px", margin: "0 auto" }}> */}
          {Object.keys(chartData).length > 0 ? (
            <Bar data={chartData} options={barChartOptions} />
          ) : (
            <p>Loading bar chart data...</p>
          )}
          {/* </div> */}
          <hr />
          <h5>Projects Implemented vs Non-Implemented ({year})</h5>
          <div
            className="flex justify-center items-center"
            style={{ height: "60vh", marginLeft: 250 }}
          >
            {Object.keys(pieChartData).length > 0 ? (
              <Pie data={pieChartData} options={pieChartOptions} />
            ) : (
              <p>Loading pie chart data...</p>
            )}
          </div>

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table1-${year}`}
            assessmentStatus={assessmentStatus}
            assessmentStatus1={assessmentStatus1}
            normalizedUserRole={normalizedUserRole}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Annex2a;